import {
  PayloadAction,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import {
  FileFolderType,
  FolderRootPath,
  LspActions,
  MODEL_TAB_PATH,
  ROLES_TAB_PATH,
  HTTP_HANDLERS_PATH,
  LOCALIZATION_PATH,
  getFileExtentionFromType,
  lspSubject,
  toastSubject,
} from '@qodly/shared';
import { get, merge } from 'lodash';
import {
  createNewFile,
  createNewNode,
  duplicateNode,
  fetchFolderContent,
  fetchFolderContentWithoutExpanding,
  fetchHTTPHandlers,
  openFile,
  relocateNode,
  renameFile,
} from './thunks';
import { ITreeItem } from './types';
import {
  getEditors,
  getFileNameWithoutExtension,
  getNodeRelativePath,
} from './utils';
import { getCashedPrefrences } from '~store/utils';

const sortFileStructure = (nodes: ITreeItem[]) => {
  const sortOrder = ['folder', 'file'];

  return nodes.sort((prev, curr) => {
    if (prev.type === curr.type) {
      // If the elements both have the same `type`,
      return prev.name.localeCompare(curr.name); // Compare the elements by `name`.
    } else {
      // Otherwise,
      return sortOrder.indexOf(prev.type) - sortOrder.indexOf(curr.type); // Substract indexes, If element `a` comes first in the array, the returned value will be negative, resulting in it being sorted before `b`, and vice versa.
    }
  });
};

const modifyTree = (tree: ITreeItem[], newNode: ITreeItem): ITreeItem[] => {
  return tree.reduce((acc: ITreeItem[], curr: ITreeItem) => {
    if (
      curr.path === newNode.path &&
      curr.type === newNode.type &&
      curr.id === newNode.id
    ) {
      return [...acc, newNode];
    }

    if (curr.children) {
      return [
        ...acc,
        {
          ...curr,
          children: sortFileStructure(modifyTree(curr.children, newNode)),
        },
      ];
    }

    return [...acc, curr];
  }, []);
};

export const findNodeByPath = (
  tree: ITreeItem[],
  path: string,
): ITreeItem | null => {
  return tree.reduce(
    (acc: ITreeItem | null, curr: ITreeItem): ITreeItem | null => {
      if (acc) {
        return acc;
      }

      if (curr.path === path) {
        return curr;
      }

      if (curr.children) {
        return findNodeByPath(curr.children, path);
      }

      return null;
    },
    null,
  );
};

const INITIAL_STATE: ITreeItem[] = [
  {
    name: FolderRootPath.WEBFORMS,
    type: FileFolderType.FOLDER,
    children: [],
    path: FolderRootPath.WEBFORMS,
    error: null,
    flags: {
      expanded: false,
      loading: false,
      cached: false,
    },
    tipKey: 'editors:webform:explorer:webforms',
    config: {},
    isRoot: true,
  },
  {
    name: FolderRootPath.METHODS,
    type: FileFolderType.FOLDER,
    children: [],
    path: FolderRootPath.METHODS,
    error: null,
    flags: {
      expanded: false,
      loading: false,
      cached: false,
    },
    config: {},
    isRoot: true,
  },
  {
    name: FolderRootPath.CLASSES,
    type: FileFolderType.FOLDER,
    children: [],
    path: FolderRootPath.CLASSES,
    error: null,
    flags: {
      expanded: false,
      loading: false,
      cached: false,
    },
    config: {},
    isRoot: true,
  },
  {
    name: FolderRootPath.SHARED,
    type: FileFolderType.FOLDER,
    children: [],
    path: FolderRootPath.SHARED,
    error: null,
    flags: {
      expanded: false,
      loading: false,
      cached: false,
    },
    config: {},
    isRoot: true,
  },
  {
    name: 'Debugger',
    type: FileFolderType.FOLDER,
    children: [],
    path: 'Debugger',
    error: null,
    flags: {
      expanded: false,
      cached: true,
    },
    config: {},
    isRoot: true,
  },
  {
    name: 'Roles And Privileges',
    type: FileFolderType.FILE,
    ext: 'json',
    path: ROLES_TAB_PATH,
    error: null,
    flags: {},
    config: {
      editor: {
        key: 'editors:roles',
      },
      supportedEditors: [
        {
          key: 'editors:code',
          config: {
            language: 'json',
            theme: 'vs-dark',
          },
        },
      ],
    },
    isRoot: true,
  },
  {
    name: 'Model',
    type: FileFolderType.MODEL,
    ext: '4DModel',
    path: MODEL_TAB_PATH,
    error: null,
    flags: {},
    config: {
      editor: {
        key: 'editors:model',
      },
      supportedEditors: [
        {
          key: 'editors:code',
          config: {
            language: 'json',
            theme: 'vs-dark',
          },
        },
      ],
    },
  },
  {
    name: 'HTTP Handlers',
    type: FileFolderType.HTTP_HANDLERS,
    ext: 'json',
    path: HTTP_HANDLERS_PATH,
    error: null,
    flags: {},
    config: {
      editor: {
        key: 'editors:http-handlers',
      },
      supportedEditors: [
        {
          key: 'editors:code',
          config: { language: 'json' },
        },
      ],
    },
  },
  {
    name: 'Localization',
    type: FileFolderType.LOCALIZATION,
    ext: 'json',
    path: LOCALIZATION_PATH,
    error: null,
    flags: {},
    config: {
      editor: {
        key: 'editors:localization',
      },
    },
    isRoot: true,
  },
  {
    name: 'Settings',
    type: FileFolderType.FILE,
    ext: 'json',
    path: 'qodlyApp.json',
    error: null,
    flags: {},
    config: {
      editor: {
        key: 'editors:settings',
      },
    },
    isRoot: true,
  },
];

const prefs = getCashedPrefrences();
const isReportsEnabled = Boolean(get(prefs, 'features.experimental.reports'));

if (isReportsEnabled) {
  INITIAL_STATE.unshift({
    name: FolderRootPath.REPORTS,
    type: FileFolderType.FOLDER,
    children: [],
    path: FolderRootPath.REPORTS,
    error: null,
    flags: {
      expanded: false,
      loading: false,
      cached: false,
    },
    tipKey: 'editors:report:explorer:reports',
    config: {},
    isRoot: true,
  });
}

const stateSlice = createSlice({
  name: 'explorer',
  initialState: INITIAL_STATE,
  reducers: {
    renameClassesFolder: (
      state: ITreeItem[],
      action: PayloadAction<{ newName: string; prevName: string }[]>,
    ) => {
      const { payload } = action;

      const classes = state.find((st) => st.name === 'Classes');
      if (!classes || !classes.children || classes.children.length === 0)
        return state;

      const index = state.findIndex((e) => e.name === 'Classes');

      const getFilename = (kind: string, name: string) => {
        switch (kind) {
          case 'dataclass': {
            return name;
          }
          case 'entity': {
            return `${name}Entity`;
          }
          default:
            return `${name}Selection`;
        }
      };

      payload.forEach(({ newName, prevName }) => {
        classes.children = classes.children?.map((child) => {
          if (child.path.endsWith('[<QODLY_ID>]') && child.name === prevName) {
            return {
              ...child,
              name: newName,
              path: `Classes/${newName}___[<QODLY_ID>]`,
              children: child.children?.map((c) => ({
                ...c,
                path: c.path.replace(prevName, newName),
                info: {
                  ...c.info,
                  filename: getFilename(c.info?.kind, newName),
                  dataclass: newName,
                },
              })),
            };
          }

          return child;
        });
      });

      state[index] = classes;

      return state;
    },
    removeClassesFolder: (state, action: PayloadAction<string[]>) => {
      const { payload: dsClassesNames } = action;

      if (!dsClassesNames || dsClassesNames.length === 0) return state;

      const classes = state.find((st) => st.name === 'Classes');
      if (!classes?.children || classes.children.length === 0) return state;

      const index = state.findIndex((e) => e.name === 'Classes');

      // should find the folder
      let toAddAsUSerclass: ITreeItem[] = [];
      dsClassesNames.forEach((dsClassName) => {
        const found = classes.children?.find(
          (child) => child.name === dsClassName,
        );

        if (found) {
          let toAdd = found.children?.filter((f) => f.info?.exist);

          if (toAdd && toAdd?.length > 0) {
            toAdd = toAdd.map((a) => ({
              ...a,
              name: a.info?.filename,
              info: {
                ...a.info,
                kind: 'userClass',
              },
            }));

            toAddAsUSerclass = toAddAsUSerclass.concat(toAdd);
          }
        }
      });
      classes.children = classes.children.filter(
        (child) => !dsClassesNames.includes(child.name),
      );

      classes.children = classes.children.concat(toAddAsUSerclass);

      classes.children.sort((a, b) => {
        if (a.name === 'DataStore') return -1;
        if (b.name === 'DataStore') return 1;

        if (
          a.type === FileFolderType.CLASS_FOLDER &&
          b.type === FileFolderType.CLASS_FOLDER
        ) {
          return a.name.localeCompare(b.name);
        }

        if (
          a.type === FileFolderType.CLASS &&
          b.type === FileFolderType.CLASS
        ) {
          return a.name.localeCompare(b.name);
        }

        if (
          a.type === FileFolderType.CLASS_FOLDER &&
          b.type !== FileFolderType.CLASS_FOLDER
        )
          return -1;
        return 1;
      });

      state[index] = classes;

      return state;
    },
    addClassesFolder: (state, action: PayloadAction<ITreeItem[]>) => {
      const { payload } = action;

      if (payload.length === 0) return state;

      const classes = state.find((ex) => ex.name === 'Classes');
      if (!classes?.children || classes.children.length === 0) return state;
      const index = state.findIndex((e) => e.name === 'Classes');

      let lastIndex = 0;
      classes.children.forEach((child, index) => {
        if (child.type === FileFolderType.CLASS_FOLDER) {
          lastIndex = index;
        }
      });

      // filter all pre-existing data class files
      payload.forEach((folder) => {
        classes.children = classes.children?.filter((cl) => {
          const found = folder.children?.find(
            (childFolder) => childFolder.info?.filename === cl.name,
          );

          if (found) {
            folder.children = folder.children?.map((child) => {
              if (child.info?.filename === cl.name) {
                return {
                  ...child,
                  info: {
                    ...child.info,
                    exist: true,
                  },
                };
              }
              return child;
            });
          }

          return !found;
        });
      });

      payload.forEach((folder) => {
        classes?.children?.splice(lastIndex + 1, 0, folder);
      });
      classes.children.sort((a, b) => {
        if (a.name === 'DataStore') return -1;
        if (b.name === 'DataStore') return 1;

        if (
          a.type === FileFolderType.CLASS_FOLDER &&
          b.type === FileFolderType.CLASS_FOLDER
        ) {
          return a.name.localeCompare(b.name);
        }

        if (
          a.type === FileFolderType.CLASS_FOLDER &&
          b.type !== FileFolderType.CLASS_FOLDER
        )
          return -1;
        return 1;
      });

      state[index] = classes;

      return state;
    },
    toggleExpandFolder: (state, action: PayloadAction<ITreeItem>) => {
      state = modifyTree(state, {
        ...action.payload,
        flags: {
          ...action.payload.flags,
          expanded: !action.payload.flags.expanded,
        },
      });

      return state;
    },
    expandFolder: (state, action: PayloadAction<ITreeItem>) => {
      state = modifyTree(state, {
        ...action.payload,
        flags: {
          ...action.payload.flags,
          expanded: true,
        },
      });
      return state;
    },
    openAddFileInput: (state, action: PayloadAction<ITreeItem>) => {
      return modifyTree(state, {
        ...action.payload,
        flags: {
          ...action.payload.flags,
          adding: true,
        },
      });
    },
    addNewFile: (
      state,
      action: PayloadAction<{ parent: ITreeItem; child: ITreeItem }>,
    ) => {
      const { parent, child } = action.payload;

      return modifyTree(state, {
        ...parent,
        children: [child, ...(parent.children || [])],
      });
    },
    removeFile: (
      state,
      action: PayloadAction<{ parent: ITreeItem; child: ITreeItem }>,
    ) => {
      const { parent, child } = action.payload;

      return modifyTree(state, {
        ...parent,
        children: parent.children!.filter((c) => c.path !== child.path),
      });
    },
    refreshFile: (
      state,
      action: PayloadAction<{ parent: ITreeItem; child: ITreeItem }>,
    ) => {
      const { parent, child } = action.payload;

      const newParent = (parent.children || []).filter(
        (c) => c.path !== child.path && !child.error,
      );

      return modifyTree(state, {
        ...parent,
        children: [child, ...newParent],
      });
    },
    setFolderChildren: (
      state,
      action: PayloadAction<{
        parentPath: string;
        children: ITreeItem[];
      }>,
    ) => {
      const parent = findNodeByPath(state, action.payload.parentPath);

      if (!parent) return state;

      return modifyTree(state, {
        ...parent,
        children: action.payload.children,
      });
    },
  },
  extraReducers: (reducer) => {
    reducer.addCase(renameFile.pending, (state, action) => {
      const { file, newname } = action.meta.arg;
      const node = findNodeByPath(state, file.path);

      if (!node) return state;

      const chunks = file.path.split('/');
      chunks.pop();
      const oldPath = `${chunks.join('/')}/${file.name}.${file.ext}`;
      const newPath = `${chunks.join('/')}/${newname}.${file.ext}`;
      switch (file.ext) {
        case '4dm':
        case '4qs':
          lspSubject.next({
            action: LspActions.WILL_RENAME,
            payload: {
              oldPath,
              newPath,
            },
          });
          break;
      }

      return modifyTree(state, {
        ...node,
        flags: { ...node.flags, loading: true },
      });
    });

    reducer.addCase(renameFile.fulfilled, (state, action) => {
      const {
        meta: {
          arg: { file },
        },
        payload,
      } = action;
      const node = findNodeByPath(state, file.path);

      if (!node) return state;

      switch (payload.old.ext) {
        case '4dm':
        case '4qs':
          lspSubject.next({
            action: LspActions.DID_RENAME,
            payload: {
              oldPath: payload.old.path,
              newPath: payload.new.path,
            },
          });
          break;
      }

      return modifyTree(state, {
        ...node,
        flags: { ...node.flags, loading: false },
      });
    });

    reducer.addCase(renameFile.rejected, (state, action) => {
      const { file } = action.meta.arg;
      const node = findNodeByPath(state, file.path);

      if (!node) return state;

      return modifyTree(state, {
        ...node,
        flags: { ...node.flags, loading: false },
      });
    });

    reducer.addCase(fetchFolderContent.pending, (state, action) => {
      const path =
        typeof action.meta.arg === 'string'
          ? action.meta.arg
          : action.meta.arg.nodePath;
      const node = findNodeByPath(state, path);

      if (!node) return state;

      return modifyTree(state, {
        ...node,
        flags: { ...node.flags, loading: true },
      });
    });

    reducer.addCase(fetchFolderContent.fulfilled, (state, action) => {
      const path =
        typeof action.meta.arg === 'string'
          ? action.meta.arg
          : action.meta.arg.nodePath;
      const node = findNodeByPath(state, path);

      if (!node) return state;

      return modifyTree(state, {
        ...node,
        children: sortFileStructure(action.payload),
        flags: {
          ...node.flags,
          loading: false,
          cached: true,
          expanded: true,
        },
        error: null,
      });
    });

    reducer.addCase(fetchFolderContent.rejected, (state, action) => {
      const path =
        typeof action.meta.arg === 'string'
          ? action.meta.arg
          : action.meta.arg.nodePath;
      const node = findNodeByPath(state, path);

      if (!node) return state;

      return modifyTree(state, {
        ...node,
        error: { message: action.payload as string },
        flags: {
          ...node.flags,
          loading: false,
          cached: true,
          expanded: true,
        },
      });
    });

    reducer.addCase(
      fetchFolderContentWithoutExpanding.pending,
      (state, action) => {
        const { nodePath } =
          typeof action.meta.arg === 'string'
            ? { nodePath: action.meta.arg }
            : action.meta.arg;
        const node = findNodeByPath(state, nodePath);

        if (!node) return state;

        return modifyTree(state, {
          ...node,
          flags: { ...node.flags, loading: true },
        });
      },
    );

    reducer.addCase(
      fetchFolderContentWithoutExpanding.fulfilled,
      (state, action) => {
        const { nodePath, collapseIfExpanded = true } =
          typeof action.meta.arg === 'string'
            ? { nodePath: action.meta.arg }
            : action.meta.arg;
        const node = findNodeByPath(state, nodePath);

        if (!node) return state;

        return modifyTree(state, {
          ...node,
          children: sortFileStructure(action.payload),
          flags: {
            ...node.flags,
            loading: false,
            cached: true,
            ...(collapseIfExpanded
              ? {
                  expanded: false,
                }
              : {}),
          },
          error: null,
        });
      },
    );

    reducer.addCase(
      fetchFolderContentWithoutExpanding.rejected,
      (state, action) => {
        const { nodePath, collapseIfExpanded = true } =
          typeof action.meta.arg === 'string'
            ? { nodePath: action.meta.arg }
            : action.meta.arg;
        const node = findNodeByPath(state, nodePath);

        if (!node) return state;

        return modifyTree(state, {
          ...node,
          error: { message: action.payload as string },
          flags: {
            ...node.flags,
            loading: false,
            cached: true,
            ...(collapseIfExpanded
              ? {
                  expanded: false,
                }
              : {}),
          },
        });
      },
    );

    reducer.addCase(createNewNode.pending, (state, action) => {
      const { fileName, parentNode, path, type } = action.meta.arg;
      const ext = getFileExtentionFromType(type);
      const filePath = `${path}/${fileName}${ext ? `.${ext}` : ''}`;

      return modifyTree(state, {
        ...parentNode,
        children: [
          {
            name: fileName,
            path: filePath,
            type,
            error: null,
            flags: {
              loading: true,
            },
            config: {},
          },
          ...parentNode.children!,
        ],
      });
    });

    reducer.addCase(createNewNode.fulfilled, (state, action) => {
      const { path, fileName, type } = action.meta.arg;
      const ext = getFileExtentionFromType(type);
      const filePath = `${path}${ext ? `.${ext}` : ''}`;
      const { editor, supportedEditors } = getEditors(filePath);

      return modifyTree(state, {
        id: `${filePath}-${Date.now()}`,
        name: fileName,
        path: filePath,
        type,
        error: null,
        flags: {
          loading: false,
        },
        config: { editor, supportedEditors },
      });
    });

    reducer.addCase(createNewNode.rejected, (state, action) => {
      const { path, fileName, type } = action.meta.arg;
      const ext = getFileExtentionFromType(type);
      const filePath = `${path}${ext ? `.${ext}` : ''}`;

      return modifyTree(state, {
        name: fileName,
        path: filePath,
        type,
        error: { message: action.payload as string },
        flags: {
          loading: false,
        },
        config: {},
      });
    });

    reducer.addCase(duplicateNode.pending, (state, action) => {
      const { node, parentNode } = action.meta.arg;
      const fileName = `${node.name}_copy`;
      const filePath = `${node.path}/${fileName}${
        node.ext ? `.${node.ext}` : ''
      }`;

      return modifyTree(state, {
        ...parentNode,
        children: [
          {
            name: fileName,
            path: filePath,
            type: node.type,
            error: null,
            flags: {
              loading: true,
            },
            permissions: {
              canRename: true,
              canDelete: true,
            },
          },
          ...parentNode.children!,
        ],
      });
    });

    reducer.addCase(duplicateNode.fulfilled, (state, action) => {
      const { node } = action.payload;
      const fileName = getFileNameWithoutExtension(node.name);
      const filePath = `${node.path}/${node.name}`; // node.name already includes the extension
      const { editor, supportedEditors } = getEditors(filePath);

      return modifyTree(state, {
        name: fileName,
        path: filePath,
        type: node.type,
        error: null,
        flags: {
          loading: false,
        },
        config: { editor, supportedEditors },
      });
    });

    reducer.addCase(duplicateNode.rejected, (state, action) => {
      const message = action.payload as string;
      const { node } = action.meta.arg;
      const fileName = `${node.name}_copy`;
      const filePath = `${node.path}/${fileName}${
        node.ext ? `.${node.ext}` : ''
      }`;

      return modifyTree(state, {
        name: fileName,
        path: filePath,
        type: node.type as FileFolderType,
        error: { message },
        flags: {
          loading: false,
        },
        config: {},
      });
    });

    reducer.addCase(relocateNode.pending, () => {});

    reducer.addCase(relocateNode.fulfilled, (state, action) => {
      const { draggedNode, newLocation } = action.meta.arg;

      const parentNodePath = getNodeRelativePath(draggedNode.path);

      const parentNode = findNodeByPath(state, parentNodePath);

      const newLocationNode = findNodeByPath(state, newLocation.path);

      const filteredParentNode = parentNode?.children?.filter(
        (node) => node.path !== draggedNode.path,
      );

      const targetLength = newLocation.path.split('/').length;

      const draggedItemLength = draggedNode.path.split('/').length;

      if (targetLength >= draggedItemLength) {
        state = modifyTree(state, {
          ...parentNode!,
          children: filteredParentNode,
        });

        state = modifyTree(state, {
          ...newLocationNode!,
          children: [
            {
              ...draggedNode,
              path: `${newLocationNode!.path}/${draggedNode.name}`,
            },
            ...(newLocationNode?.children ? newLocationNode.children : []),
          ],
          flags: {
            ...newLocationNode!.flags,
            expanded: true,
          },
        });
      } else {
        state = modifyTree(state, {
          ...newLocationNode!,
          children: [
            {
              ...draggedNode,
              path: `${newLocationNode!.path}/${draggedNode.name}`,
            },
            ...(newLocationNode?.children ? newLocationNode.children : []),
          ],
          flags: {
            ...newLocationNode!.flags,
            expanded: true,
          },
        });

        state = modifyTree(state, {
          ...parentNode!,
          children: filteredParentNode,
        });
      }

      return state;
    });

    reducer.addCase(relocateNode.rejected, () => {
      toastSubject.next({
        message: 'Error moving file',
        kind: 'danger',
      });
    });

    reducer.addCase(createNewFile.pending, (state, action) => {
      const { fileName, parentNode, path, type, qodly } = action.meta.arg;

      const ext = getFileExtentionFromType(type, qodly);
      const filePath = `${path}/${fileName}${ext ? `.${ext}` : ''}`;
      const { editor, supportedEditors } = getEditors(filePath);

      switch (type) {
        case FileFolderType.CLASS:
        case FileFolderType.METHOD:
          lspSubject.next({
            action: LspActions.WILL_CREATE,
            payload: {
              filePath,
            },
          });
          break;
      }

      const parent = parentNode.children?.filter(
        (item) => item.path !== filePath,
      );

      const newChildren = [
        ...parent!,
        {
          name: fileName,
          path: filePath,
          type: type === FileFolderType.CLASS ? type : FileFolderType.FILE,
          error: null,
          flags: {
            loading: true,
          },
          config: { editor, supportedEditors },
          ext,
          date: new Date().toISOString(),
          info: {
            exist: true,
            kind: type === FileFolderType.CLASS ? 'userClass' : '',
          },
        },
      ];

      const newState = modifyTree(state, {
        ...parentNode,
        children: newChildren,
      });

      return merge(state, newState);
    });
    reducer.addCase(createNewFile.fulfilled, (state, action) => {
      const { fileName, path, type, qodly } = action.meta.arg;

      const ext = getFileExtentionFromType(type, qodly);
      const filePath = `${path}/${fileName}${ext ? `.${ext}` : ''}`;
      const { editor, supportedEditors } = getEditors(filePath);

      const newState = modifyTree(state, {
        name: fileName,
        path: filePath,
        type: FileFolderType.FILE,
        error: null,
        flags: {},
        config: { editor, supportedEditors },
        ext,
        date: new Date().toISOString(),
        info: {
          exist: true,
          kind: type === FileFolderType.CLASS ? 'userClass' : '',
        },
      });

      switch (type) {
        case FileFolderType.CLASS:
        case FileFolderType.METHOD:
          lspSubject.next({
            action: LspActions.DID_CREATE,
            payload: {
              filePath,
            },
          });
          break;
      }

      state = newState;
      return state;
    });

    reducer.addCase(createNewFile.rejected, (state, action) => {
      const { fileName, path, type } = action.meta.arg;

      const ext = getFileExtentionFromType(type);
      const filePath = `${path}${ext ? `.${ext}` : ''}`;
      const { editor, supportedEditors } = getEditors(filePath);

      const newState = modifyTree(state, {
        name: fileName,
        path: filePath,
        type: FileFolderType.FILE,
        error: { message: 'Failed creating file' },
        flags: {},
        config: { editor, supportedEditors },
        ext,
        date: new Date().toISOString(),
      });

      state = newState;
      return state;
    });

    reducer.addCase(fetchHTTPHandlers.rejected, (state) => {
      const node = findNodeByPath(state, HTTP_HANDLERS_PATH);
      if (node) {
        state = modifyTree(state, {
          ...node,
          flags: { ...node.flags, disabled: true },
        });
      }

      return state;
    });

    reducer.addMatcher(isPending(openFile), (state, action) => {
      const node = findNodeByPath(state, action.meta.arg.file.path);

      if (node) {
        state = modifyTree(state, {
          ...node,
          flags: { ...node.flags, loading: true },
        });
      }

      return state;
    });

    reducer.addMatcher(isFulfilled(openFile), (state, action) => {
      const node = findNodeByPath(state, action.meta.arg.file.path);
      const infoNodeData = node?.info
        ? {
            ...node.info,
            exist: true,
          }
        : undefined;
      if (node) {
        state = modifyTree(state, {
          ...node,
          flags: { ...node.flags, loading: false, disabled: false },
          info: infoNodeData,
        });
      }

      return state;
    });

    reducer.addMatcher(isRejected(openFile), (state, action) => {
      const node = findNodeByPath(state, action.meta.arg.file.path);

      if (node) {
        state = modifyTree(state, {
          ...node,
          flags: { ...node.flags, loading: false },
        });
      }

      toastSubject.next({
        message: 'Failed to open file, try again',
        kind: 'danger',
      });

      return state;
    });
  },
});

export const {
  toggleExpandFolder,
  expandFolder,
  openAddFileInput,
  addNewFile,
  refreshFile,
  removeFile,
  setFolderChildren,
  addClassesFolder,
  removeClassesFolder,
  renameClassesFolder,
} = stateSlice.actions;

export default stateSlice;
